/**
 * voicemailsReducer.js
 * Talkroute
 *
 * Created by Vel Genov on 2/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import { REHYDRATE } from "redux-persist";
import moment from "moment";

import {
  FETCH_VOICEMAILS_BEGIN,
  FETCH_VOICEMAILS_SUCCESS,
  FETCH_VOICEMAILS_FAILURE,
  FETCH_VOICEMAILBOXES_BEGIN,
  FETCH_VOICEMAILBOXES_SUCCESS,
  FETCH_VOICEMAILBOXES_FAILURE,
  APPLY_VOICEMAIL_FILTER,
  SHOW_VOICEMAIL_DETAILS,
  HIDE_VOICEMAIL_DETAILS,
  PLAY_VOICEMAIL,
  DELETE_VOICEMAIL_SUCCESS,
  UPDATE_READ_STATUS,
  FETCH_VOICEMAIL_FILE_SUCCESS,
  voicemailsFilterHelper,
  countUnreadVoicemails,
  findVoicemailIndexById,
  ERROR_PLAYING_VOICEMAIL,
  FETCH_VOICEMAIL_EMPTY,
} from "../actions/voicemailActions";
import { CLEAR_ERRORS } from "../actions/errorActions";
import dataStateEnum from "../constants/dataStateConstants";

import { VoicemailData, voicemailsRehydrator } from "../services/dataClasses/VoicemailData";
import { voicemailBoxesRehydrator } from "../services/dataClasses/VoicemailMailbox";
import uiString from "../constants/uiString";
import validateArray from "../helpers/arrayValidator";
import { CallHistoryData } from "@services/dataClasses";

export interface IVoicemailsReducer {
  data: VoicemailData[];
  mailboxes: any[];
  lastFetchTime: null | number;
  filterBy: string;
  filteredData: VoicemailData[];
  dataState: dataStateEnum;
  voicemailDetails: null | number;
  relatedCalls: CallHistoryData[];
  voicemailDetailsParent: null | string;
  unreadCount: number;
  playVoicemail: null | number;
  playVoicemailFilePath: null | string;
  isWelcomeArrived: boolean;
  error: null | string;
}

const initialState = {
  data: [] as any[],
  mailboxes: [],
  lastFetchTime: null,
  filterBy: uiString.ALL_MAILBOXES,
  filteredData: [],
  dataState: dataStateEnum.LOADING,
  voicemailDetails: null,
  relatedCalls: [],
  voicemailDetailsParent: null,
  unreadCount: 0,
  playVoicemail: null,
  playVoicemailFilePath: null,
  isWelcomeArrived: false,
  error: null,
};

/**
 * A redux function to process voicemail actions
 * @param {*} state the redux state
 * @param {*} action passed in action through the voicemailActions creator
 */
export default function voicemails(state: IVoicemailsReducer = initialState, action): IVoicemailsReducer {
  switch (action.type) {
    case REHYDRATE:
      // A redux-persist action. Called immediately after launch. VoicemailData objects are restored as generic Object by default.
      // Using a rehydrator function to restore to VoicemailData objects. Rehydrator checks for empty objects.
      if (action.payload !== undefined) {
        return {
          ...state,
          data: voicemailsRehydrator(action.payload.voicemails.data),
          mailboxes: voicemailBoxesRehydrator(
            action.payload.voicemails.mailboxes
          ),
          filteredData: voicemailsRehydrator(action.payload.voicemails.data),
          unreadCount: action.payload.voicemails.unreadCount,
          isWelcomeArrived: action.payload.voicemails.isWelcomeArrived,
        };
      }

      return {
        ...state,
      };

    case FETCH_VOICEMAILS_BEGIN:
      // Mark the state as LOADING so we can show a the user data is being loaded
      // Also, reset any errors. We're starting fresh.
      return {
        ...state,
        dataState: dataStateEnum.LOADING,
        error: null,
      };

    case FETCH_VOICEMAILS_SUCCESS: {
      // Successfully retrieved voicemail data

      const filteredData = voicemailsFilterHelper(
        action.payload.voicemails.data,
        state.filterBy
      );

      const unreadCount = countUnreadVoicemails(action.payload.voicemails.data);

      const playVoicemail = action.payload.voicemails.data.find(
        (mail) => mail.id === state.playVoicemail
      );

      const currentTimestamp = moment().unix();
      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: action.payload.voicemails.data,
        lastFetchTime: currentTimestamp,
        filteredData,
        unreadCount,
        isWelcomeArrived: true,
        playVoicemail: playVoicemail ? playVoicemail.id : null,
        playVoicemailFilePath: playVoicemail
          ? state.playVoicemailFilePath
          : null,
        error: null
      };
    }

    case FETCH_VOICEMAILS_FAILURE:
      // An error occured while loading the data
      return {
        ...state,
        dataState: dataStateEnum.ERROR,
        error: action.payload.error,
      };

    case FETCH_VOICEMAILBOXES_BEGIN:
      // Started fetching the voicemail mailboxes
      return {
        ...state,
        error: null,
      };

    case FETCH_VOICEMAILBOXES_SUCCESS:
      // Successfully fetched the voicemail mailboxes
      return {
        ...state,
        mailboxes: action.payload.voicemailBoxes.data,
      };

    case FETCH_VOICEMAILBOXES_FAILURE:
      // Error fetching the voicemail mailboxes
      return {
        ...state,
        error: action.payload.error,
      };

    case PLAY_VOICEMAIL:
      // Start playing a voicemail with ID
      return {
        ...state,
        playVoicemail: action.payload.playVoicemail,
        playVoicemailFilePath: null,
      };

    case SHOW_VOICEMAIL_DETAILS:
      // Shows details about a voicemail
      return {
        ...state,
        voicemailDetails: action.payload.id,
        playVoicemail: action.payload.id,
        playVoicemailFilePath: null,
        relatedCalls: action.payload.topRelatedCalls,
        voicemailDetailsParent: action.payload.parent,
      };

    case HIDE_VOICEMAIL_DETAILS:
      // Hides details about a voicemail
      return {
        ...state,
        voicemailDetails: action.payload.voicemail,
        relatedCalls: action.payload.topRelatedCalls,
        voicemailDetailsParent: action.payload.parent,
      };

    case APPLY_VOICEMAIL_FILTER:
      // A filter was applied to the voicemai mailboxes
      return {
        ...state,
        filteredData: action.payload.filteredVoicemails,
        filterBy: action.payload.filterBy,
      };

    case DELETE_VOICEMAIL_SUCCESS: {
      // Delete a voicemail locally

      const { voicemailId } = action.payload;
      const updatedData = deleteVoicemailLocal({
        voicemails: state.data,
        voicemailId,
      });

      const unreadCount = countUnreadVoicemails(updatedData);
      const filteredData = voicemailsFilterHelper(updatedData, state.filterBy);

      let selectedVoicemail = state.voicemailDetails;
      if (selectedVoicemail === voicemailId) {
        // The currently selected voicemail was deleted. Change selected to the next one, or none if no voicemails are available
        selectedVoicemail = null;

        if (validateArray(updatedData)) {
          const prevVoicemailIndex = state.data.findIndex(item => item.id===voicemailId); 
          const newSelectedVoicemail = updatedData[(prevVoicemailIndex-1) >=0 ? prevVoicemailIndex - 1 : 0];
          if (newSelectedVoicemail) {
            const { id: nextVoicemailId } = newSelectedVoicemail;

            selectedVoicemail = nextVoicemailId;
          }
        }
      }

      return {
        ...state,
        data: updatedData,
        filteredData,
        unreadCount,
        voicemailDetails: selectedVoicemail,
        playVoicemail: selectedVoicemail,
        playVoicemailFilePath: null,
      };
    }

    case UPDATE_READ_STATUS: {
      // Update the read status of a voicemail
      const { voicemailId, readStatus } = action.payload;
      const updatedData = updateReadStatusLocal({
        voicemails: state.data,
        voicemailId,
        readStatus,
      });

      const unreadCount = countUnreadVoicemails(updatedData);
      const filteredData = voicemailsFilterHelper(updatedData, state.filterBy);

      return {
        ...state,
        data: updatedData,
        filteredData,
        unreadCount,
      };
    }

    case FETCH_VOICEMAIL_FILE_SUCCESS: {
      // Successfully fetched a voicemail file
      const { voicemailFilePath } = action.payload;
      return {
        ...state,
        playVoicemailFilePath: voicemailFilePath.data,
      };
    }

    case ERROR_PLAYING_VOICEMAIL: {
      // An error was raised while trying to play a voicemail
      return {
        ...state,
        error: action.payload.error,
      };
    }

    case CLEAR_ERRORS:
      // Clear any errors
      return {
        ...state,
        error: null,
      };

    case FETCH_VOICEMAIL_EMPTY:
      // An empty redux action. Use the previous state
      return {
        ...state,
      };

    default:
      // ALWAYS have a default case in a reducer
      return state;
  }
}

/**
 * Delete a voicemail from the local data instance
 *
 * @param voicemails the voicemails to delete from
 * @param voicemailId the id of the voicemail to be deleted
 * @returns an updated copy of the voicemails
 */
const deleteVoicemailLocal = (props) => {
  const { voicemails: voicemailsLocal, voicemailId } = props;
  if (voicemailsLocal && voicemailId) {
    const foundIndex = findVoicemailIndexById({
      voicemails: voicemailsLocal,
      voicemailId,
    });
    // Remove 1 element from foundIndex, and create a new array
    if (foundIndex === -1) {
      return voicemailsLocal;
    }
    const updatedVoicemails = Array.from(voicemailsLocal);
    updatedVoicemails.splice(foundIndex, 1);
    return updatedVoicemails;
  }
  return voicemailsLocal;
};

/**
 * Update the read status of the voicemails local instance
 *
 * @param voicemails the array of voicemails to search in
 * @param voicemailId the voicemail to update
 * @param readStatus the updated read status
 * @returns an updated copy of the voicemails
 */
export const updateReadStatusLocal = (props) => {
  const { voicemails: voicemailsLocal, voicemailId, readStatus } = props;
  const newVoicemails = voicemailsLocal?.slice();

  if (newVoicemails && voicemailId) {
    return voicemailsRehydrator(newVoicemails.map(voicemail => {
      if (voicemail.id !== voicemailId) return voicemail;
      else return {
        ...voicemail,
        messageRead: readStatus
      }
    }));
  }
  return newVoicemails;
};
